import { useCallback } from 'react';

import { EApiRoutes, getApiRouteOrMock } from '../api/api.utils';
import { EDashboardPage, EEventsType, ELocalStorageKeys, ELockoutSource } from '../constants/enums';
import { localStorageUtil } from '../utils/localStorageUtil';

import useAxios from './useAxios';

export interface BigQueryEventData {
  eventName: EEventsType;
  publisherId?: string;
  userId?: string;
  userEmail?: string;
  countryCode?: string;
  annualRev?: string;
  teamSize?: string;
  websiteUrl?: string;
  isCheckboxApproved?: boolean;
  phone?: string;
  taskName?: string;
  taskStatus?: string;
  method?: string;
  templateId?: string;
  isActiveTemplate?: boolean;
  templateAction?: string;
}

export interface PreLoginDashboardEventData {
  page: EDashboardPage;
  userEmail?: string;
  userId?: string;
  source?: ELockoutSource;
  cooldownTime?: string;
}

interface BaseEventPayload {
  publisherId?: string;
  userId?: string;
  userEmail?: string;
}

export const useBigQueryEventsTracking = () => {
  const axios = useAxios();

  const getBaseEventData = useCallback(
    (overrides?: Partial<BaseEventPayload>): BaseEventPayload => {
      const userDetails = localStorageUtil.getAny(
        ELocalStorageKeys.USER_DETAILS
      );
      const publisherIdFromStorage = localStorageUtil.getAny<string>(
        ELocalStorageKeys.PUBLISHER_ID
      );

      const publisherId = overrides?.publisherId || publisherIdFromStorage;
      const userId = overrides?.userId || userDetails?.userId;
      const userEmail = overrides?.userEmail || userDetails?.email;

      return {
        ...(publisherId ? { publisherId } : {}),
        ...(userId ? { userId } : {}),
        ...(userEmail ? { userEmail } : {})
      };
    },
    []
  );

  const trackBigQueryEvent = useCallback(
    async (eventData: BigQueryEventData) => {
      try {
        if (!eventData.eventName) {
          console.warn('Event name is required for BigQuery tracking');
          return;
        }

        const baseData = getBaseEventData({
          publisherId: eventData.publisherId,
          userId: eventData.userId,
          userEmail: eventData.userEmail
        });

        const payload = {
          ...baseData,
          eventName: eventData.eventName,
          ...(eventData.countryCode && { countryCode: eventData.countryCode }),
          ...(eventData.annualRev && { annualRev: eventData.annualRev }),
          ...(eventData.teamSize && { teamSize: eventData.teamSize }),
          ...(eventData.websiteUrl && { websiteUrl: eventData.websiteUrl }),
          ...(eventData.isCheckboxApproved !== undefined && {
            isCheckboxApproved: eventData.isCheckboxApproved
          }),
          ...(eventData.phone && { phone: eventData.phone }),
          ...(eventData.taskName && { taskName: eventData.taskName }),
          ...(eventData.taskStatus && { taskStatus: eventData.taskStatus }),
          ...(eventData.method && { method: eventData.method }),
          ...(eventData.templateId && { templateId: eventData.templateId }),
          ...(eventData.templateAction && {
            templateAction: eventData.templateAction
          }),
          ...(eventData.isActiveTemplate && {
            isActiveTemplate: eventData.isActiveTemplate
          })
        };

        await axios.post(
          getApiRouteOrMock(EApiRoutes.PUBLISHER_DASHBOARD_ACTIVITIES),
          payload
        );
      } catch (error) {
        console.error('Failed to send BigQuery event:', error);
      }
    },
    [axios, getBaseEventData]
  );

  const trackPreLoginEvent = useCallback(
    async (eventData: PreLoginDashboardEventData) => {
      try {
        if (!eventData.page) {
          console.warn('Page is required for pre-login tracking');
          return;
        }

        const baseData = getBaseEventData({
          userId: eventData.userId,
          userEmail: eventData.userEmail
        });

        const payload = {
          ...baseData,
          eventName: EEventsType.DASHBOARD_PAGE_DISPLAYED,
          page: eventData.page,
          ...(eventData.source && { source: eventData.source }),
          ...(eventData.cooldownTime !== undefined && {
            cooldownTime: eventData.cooldownTime
          })
        };

        await axios.post(
          getApiRouteOrMock(EApiRoutes.PRE_LOGIN_DASHBOARD_ACTIVITIES),
          payload
        );
      } catch (error) {
        console.error('Failed to send pre-login event:', error);
      }
    },
    [axios, getBaseEventData]
  );

  return {
    trackBigQueryEvent,
    trackPreLoginEvent
  };
};
